import type { LivePriceDto } from "@intelligems/ig-types";

import type { BillingModule, BillingPlan } from "./types";
import {
  buildBillingCatalog,
  currentCadence,
  currentLiveSubscription,
  entityKeys,
  entityPriceIdsOnSubscription,
  presentedActiveModules,
  productsForEntity,
  riderPriceIdsOnSubscription,
  subscriptionCadence,
  trialingPriceIdsOnSubscription,
  unaccountedSubscriptionPriceIds,
} from "./catalog";
import {
  MODULE_STATIC_CONFIG,
  PRODUCT_VERSION_2026,
  SMART_PRICING_VARIANTS,
  UNLIMITED_ID,
} from "./constants";
import type {
  BillingCommandAnalyticsEvent,
  BillingCommandCompletionChangeType,
  BillingCommandFeedback,
  BillingCommandOperation,
  BillingCommandPlan,
  BillingCommandPlannerInput,
  BlockedBillingCommand,
  OptimisticAccessProduct,
  PlannedBillingCommand,
} from "./commandTypes";
import {
  getCommittedSelection,
  getCurrentSelection,
  quoteSelection,
} from "./pricing";
import { isSelectionNoOp } from "./selection";
import type {
  BillingCatalog,
  BillingCycle,
  BillingSnapshot,
  RawSubscriptionAccess,
  RawUpgradeDowngradePath,
  SelectionState,
} from "./types";

type Subscription = RawSubscriptionAccess["subscriptions"]["active"][number];
type BillingEntity = BillingModule | BillingPlan;
type ProductSetOperation = Extract<
  BillingCommandOperation,
  "add-module" | "remove-module" | "cancel-trial" | "upgrade-smart-pricing"
>;
type MutableAnalytics = {
  onError: BillingCommandAnalyticsEvent[];
  onStart: BillingCommandAnalyticsEvent[];
  onSuccess: BillingCommandAnalyticsEvent[];
};

const PLAN_SAVED: BillingCommandFeedback = {
  message: "Plan changes saved",
  tone: "success",
  confetti: true,
};

const PLAN_SAVED_QUIET: BillingCommandFeedback = {
  ...PLAN_SAVED,
  confetti: false,
};

const NO_CHANGES: BillingCommandPlan = {
  kind: "no-op",
  feedback: {
    message: "No changes to save.",
    tone: "info",
    confetti: false,
  },
};

const errorFeedback = (message: string): BillingCommandFeedback => ({
  message,
  tone: "error",
  confetti: false,
});

const successFeedback = (message: string): BillingCommandFeedback => ({
  message,
  tone: "success",
  confetti: false,
});

const checkoutError = ({
  errorMessage,
  errorType,
  step,
}: {
  errorMessage: string;
  errorType: string;
  step: Extract<
    BillingCommandAnalyticsEvent,
    { type: "billing-checkout-error-shown" }
  >["step"];
}): BillingCommandAnalyticsEvent => ({
  type: "billing-checkout-error-shown",
  errorMessage,
  errorType,
  step,
});

const guard = ({
  errorMessage,
  reason,
  step,
}: {
  errorMessage: string;
  reason: BlockedBillingCommand["reason"];
  step: Extract<
    BillingCommandAnalyticsEvent,
    { type: "billing-checkout-error-shown" }
  >["step"];
}): BlockedBillingCommand => ({
  kind: "blocked",
  reason,
  feedback: errorFeedback(
    "Something went wrong. Please refresh and try again."
  ),
  analytics: [
    checkoutError({ errorType: "guard_failure", errorMessage, step }),
  ],
});

const operationBlocked = ({
  analytics,
  message,
  reason,
}: {
  analytics: readonly BillingCommandAnalyticsEvent[];
  message: string;
  reason: BlockedBillingCommand["reason"];
}): BlockedBillingCommand => ({
  kind: "blocked",
  reason,
  feedback: errorFeedback(message),
  analytics,
});

const emptyAnalytics = (): MutableAnalytics => ({
  onStart: [],
  onSuccess: [],
  onError: [],
});

const billingCommandInProgress = (snapshot: BillingSnapshot): boolean =>
  snapshot.subscriptionAccessData.upgradeDowngradePaths.blocked.some(
    ({ decision }) => decision.reason === "billing_command_in_progress"
  );

const commandInProgressPlan = (): BlockedBillingCommand => ({
  kind: "blocked",
  reason: "billing_command_in_progress",
  analytics: [],
  feedback: {
    tone: "dialog",
    title: "Billing change in progress",
    body: "Your recent billing change is still being applied. Once it completes, you'll be able to make this change.",
    acknowledgeLabel: "Got it",
  },
});

const subscriptionLink = (
  snapshot: BillingSnapshot,
  subscription: Subscription
) =>
  snapshot.subscriptionLinks.find(
    ({ stripeSubscriptionId }) =>
      stripeSubscriptionId === subscription.stripeSubscriptionId
  );

const cadenceName = (cadence: BillingCycle): "annual" | "monthly" =>
  cadence === "year" ? "annual" : "monthly";

const unique = (values: readonly string[]): string[] => [...new Set(values)];

const entityInCatalog = (
  catalog: BillingCatalog,
  entityId: string
): BillingEntity | undefined =>
  [...catalog.plans, ...catalog.modules].find(({ id }) => id === entityId);

const livePriceForEntity = (
  snapshot: BillingSnapshot,
  entity: BillingEntity,
  cadence: BillingCycle
): LivePriceDto | undefined => {
  if (!entity.stripePriceId) {
    return undefined;
  }

  const seed = snapshot.livePrices.find(
    ({ id, priceIds }) =>
      id === entity.stripePriceId || priceIds.includes(entity.stripePriceId!)
  );
  if (!seed) {
    return undefined;
  }

  return snapshot.livePrices.find(
    ({ interval, intervalCount, product }) =>
      product.id === seed.product.id &&
      interval === cadence &&
      intervalCount === 1
  );
};

const optimisticProduct = (
  livePrice: LivePriceDto,
  entityId: string
): OptimisticAccessProduct => ({
  stripeProductId: livePrice.product.id,
  name: livePrice.product.name,
  priceIds: [livePrice.id],
  entitlementProfileKey: entityId,
});

const subscriptionForEntity = ({
  catalog,
  entityId,
  snapshot,
}: {
  catalog: BillingCatalog;
  entityId: string;
  snapshot: BillingSnapshot;
}): Subscription | undefined => {
  const products = productsForEntity(snapshot, catalog, entityId);
  const subscription =
    snapshot.subscriptionAccessData.subscriptions.active.find(
      ({ priceIds, productIds }) =>
        products.some(
          (product) =>
            productIds.includes(product.stripeProductId) ||
            product.priceIds.some((priceId) => priceIds.includes(priceId))
        )
    );

  if (subscription) {
    return subscription;
  }

  return catalog.currentPlan?.id === entityId || entityId === "active-modules"
    ? currentLiveSubscription(snapshot)
    : undefined;
};

const targetEntitlements = ({
  addedEntityIds,
  catalog,
  removedEntityIds,
  snapshot,
}: {
  addedEntityIds: readonly string[];
  catalog: BillingCatalog;
  removedEntityIds: readonly string[];
  snapshot: BillingSnapshot;
}): string[] => {
  const removedKeys = new Set(
    removedEntityIds.flatMap((entityId) => [...entityKeys(catalog, entityId)])
  );

  return unique([
    ...snapshot.subscriptionAccessData.access.entitlementProfileKeys.filter(
      (key) => !removedKeys.has(key)
    ),
    ...addedEntityIds,
  ]);
};

const selectedProducts = ({
  catalog,
  entityIds,
  snapshot,
}: {
  catalog: BillingCatalog;
  entityIds: readonly string[];
  snapshot: BillingSnapshot;
}): OptimisticAccessProduct[] =>
  entityIds.flatMap((entityId) => {
    const entity = entityInCatalog(catalog, entityId);
    const price = entity
      ? livePriceForEntity(snapshot, entity, catalog.cadence)
      : undefined;

    return price ? [optimisticProduct(price, entityId)] : [];
  });

type BillingInterval = Readonly<{ interval: string; intervalCount: number }>;

const subscriptionBillingInterval = (
  subscription: Subscription
): BillingInterval => ({
  interval: subscription.interval,
  intervalCount: subscription.intervalCount ?? 1,
});

const offeredInterval = (cadence: BillingCycle): BillingInterval => ({
  interval: cadence,
  intervalCount: 1,
});

const priceIdForProductInInterval = (
  snapshot: BillingSnapshot,
  seedPriceId: string,
  target: BillingInterval
): string | undefined => {
  const seed = snapshot.livePrices.find(
    ({ id, priceIds }) => id === seedPriceId || priceIds.includes(seedPriceId)
  );
  if (!seed) {
    return undefined;
  }

  return snapshot.livePrices.find(
    ({ interval, intervalCount, product }) =>
      product.id === seed.product.id &&
      interval === target.interval &&
      intervalCount === target.intervalCount
  )?.id;
};

const priceIdInCadence = (
  snapshot: BillingSnapshot,
  priceId: string,
  cadence: BillingCycle
): string | undefined =>
  priceIdForProductInInterval(snapshot, priceId, offeredInterval(cadence));

/**
 * The entities a Selection asks to be billed for.
 *
 * An Unlimited Selection resolves to the plan alone: Unlimited *is* the bundle, so its
 * modules are implied by the plan price rather than itemised alongside it.
 */
const selectionEntityIds = (selection: SelectionState): string[] =>
  selection.planId ? [selection.planId] : unique(selection.moduleIds);

/**
 * Whether this submit is asking to re-price the subscription's items.
 *
 * Read off the **edited** subscription's own `interval` rather than off
 * `getCurrentSelection`, which answers for the account's primary subscription. The two
 * differ whenever a removal retargets the Command at a sibling subscription, and a
 * spurious cadence change re-prices every retained item on it.
 *
 * (`getCurrentSelection` also used to answer `"month"` for an annual `trialing` org,
 * because it matched `status === "active"` alone. That is fixed at the source now — see
 * `currentSubscriptionFor` — so this function is the narrower reading, not a workaround.)
 */
const isCadenceChangeFor = (
  subscription: Subscription,
  cadence: BillingCycle
): boolean => subscriptionCadence(subscription) !== cadence;

/**
 * The Selection's prices plus the riders the Selection cannot express, each at the price
 * the subscription already bills it on.
 *
 * Appended after the caller's empty-target check, never before it — see
 * `riderPriceIdsOnSubscription` for what counts as a rider and why dropping one would
 * silently cancel a product the merchant pays for.
 *
 * Total, because a submit that does not re-price cannot fail to place a rider. A lane that
 * may re-price calls `withRiderPriceIdsInCadence` instead.
 */
const withRiderPriceIds = (
  selectedPriceIds: readonly string[],
  snapshot: BillingSnapshot,
  subscription: Subscription
): string[] =>
  unique([
    ...selectedPriceIds,
    ...riderPriceIdsOnSubscription(snapshot, subscription),
  ]);

/**
 * `withRiderPriceIds` for a submit that may re-price, so it can fail: `null` when a rider
 * has no price at the requested cadence.
 *
 * A cadence change has to move the riders too. Stripe bills one interval per subscription,
 * so a target pairing annual modules with a rider still on its monthly price is not a plan
 * Stripe can execute — and appending the rider verbatim built exactly that. A rider
 * withheld from the live-price feed has no other cadence to resolve to, so there is no
 * target to build and the caller refuses; keeping the merchant on their current cadence is
 * the honest outcome, not submitting a payload the provider rejects.
 */
const withRiderPriceIdsInCadence = ({
  cadence,
  isCadenceChange,
  selectedPriceIds,
  snapshot,
  subscription,
}: {
  cadence: BillingCycle;
  isCadenceChange: boolean;
  selectedPriceIds: readonly string[];
  snapshot: BillingSnapshot;
  subscription: Subscription;
}): string[] | null => {
  if (!isCadenceChange) {
    return withRiderPriceIds(selectedPriceIds, snapshot, subscription);
  }

  const converted: string[] = [];
  for (const riderPriceId of riderPriceIdsOnSubscription(
    snapshot,
    subscription
  )) {
    const retained = priceIdInCadence(snapshot, riderPriceId, cadence);
    if (!retained) {
      return null;
    }
    converted.push(retained);
  }

  return unique([...selectedPriceIds, ...converted]);
};

/**
 * Refuses a target that would mix billing intervals.
 *
 * Stripe bills one interval per subscription, so a target pairing (say) annual modules
 * with a rider still on its monthly price is not a plan the provider can execute. The
 * same is true of a module added to a quarterly-billed subscription at its monthly price.
 * Both are one condition — some product has no price at the interval this subscription
 * bills on — so both report one `errorType`, and only the merchant-facing sentence
 * differs.
 */
const unconvertibleIntervalBlock = ({
  step,
  subject,
}: {
  step: "confirmation" | "module_selection" | "plan_selection";
  subject: "module" | "rider";
}): BlockedBillingCommand =>
  operationBlocked({
    reason: "unconvertible_interval",
    message:
      subject === "rider"
        ? "This plan includes an add-on we can't move to a new billing cycle. Please contact support and we'll sort it out."
        : "That module isn't available on your billing cycle. Please contact support and we'll sort it out.",
    analytics: [
      checkoutError({
        errorType: "unconvertible_interval",
        errorMessage: `Subscription ${subject} has no price at the requested billing interval, so the target would mix intervals`,
        step,
      }),
    ],
  });

const isBilledOnAnotherSubscription = ({
  catalog,
  entityId,
  snapshot,
  subscription,
}: {
  catalog: BillingCatalog;
  entityId: string;
  snapshot: BillingSnapshot;
  subscription: Subscription;
}): boolean =>
  snapshot.subscriptionAccessData.subscriptions.active.some(
    (candidate) =>
      candidate.stripeSubscriptionId !== subscription.stripeSubscriptionId &&
      entityPriceIdsOnSubscription({
        catalog,
        entityId,
        snapshot,
        subscription: candidate,
      }).length > 0
  );

type SelectionTarget =
  | Readonly<{ kind: "ok"; priceIds: string[] }>
  | Readonly<{ kind: "missing-price" }>
  | Readonly<{ kind: "interval-unavailable" }>;

/**
 * Builds the requested Product-set target for the Selection, against one subscription.
 *
 * **The Selection decides which products are on the plan. It does not decide which price
 * realizes a product the subscription already bills** — that is a fact about the live
 * item, and re-deriving it from the catalog turns "remove a module" into a silent
 * re-pricing. So: the price of an existing item is derived from that item; only a
 * genuine add is derived from the catalog. Concretely, re-deriving used to move a
 * stale-tier module onto the tier the backend advertises, and a quarterly-billed module
 * onto the monthly row, on a Command that asked for neither.
 *
 * A cadence change is the one case where re-pricing *is* the request, and even then the
 * new price is resolved from the billed one via `priceIdInCadence` (which pivots on the
 * item's own product) rather than from `entity.stripePriceId`.
 *
 * What it does NOT do is seed from `subscription.priceIds`: the projection and the raw
 * subscription can disagree about what an org owns (an `-ARCHIVED` price is billed but
 * reads as unowned), and a target seeded that way bills a plan the merchant was never
 * shown — the ENG-7560 defect. Callers refuse such a subscription up front via
 * `unaccountedSubscriptionPriceIds`, because the target is declarative and the backend
 * deletes every live item absent from it.
 *
 * Scoped to the edited subscription: one submit is one Command against one subscription,
 * and an account can split modules across several. An entity billed on a *different*
 * subscription stays out, or this Command would move or duplicate that one's item.
 *
 * Riders are appended by the caller, after its empty-target check — see
 * `riderPriceIdsOnSubscription`.
 *
 * The catalog comes in rather than being rebuilt here, and the cadence is read off it, so
 * a caller cannot hand this function a catalog and a cadence that disagree.
 */
const targetPriceIdsForSelection = ({
  catalog,
  isCadenceChange,
  revokedTrialPriceIds,
  selection,
  snapshot,
  subscription,
}: {
  catalog: BillingCatalog;
  isCadenceChange: boolean;
  revokedTrialPriceIds?: readonly string[];
  selection: SelectionState;
  snapshot: BillingSnapshot;
  subscription: Subscription;
}): SelectionTarget => {
  const cadence = catalog.cadence;
  // A submit that does not change cadence keeps the subscription's own interval,
  // multi-month included; one that does asks for a single-period cadence. Everything in
  // the target — retained, added, and the caller's riders — resolves to this one shape.
  const targetInterval = isCadenceChange
    ? offeredInterval(cadence)
    : subscriptionBillingInterval(subscription);
  const priceIds: string[] = [];

  for (const entityId of selectionEntityIds(selection)) {
    const billedHere = entityPriceIdsOnSubscription({
      catalog,
      entityId,
      snapshot,
      subscription,
    });

    if (billedHere.length > 0) {
      // More than one price for a single entity means an alias collision (a merchant
      // mid-migration billed on both `starter` and `smart-content`, say). Keep them all,
      // exactly as the subscription has them: the org is already in that state, and
      // refusing here would be one more lockout. `unique()` keeps the payload valid.
      for (const billedPriceId of billedHere) {
        const retained = isCadenceChange
          ? priceIdForProductInInterval(snapshot, billedPriceId, targetInterval)
          : billedPriceId;
        if (!retained) {
          return { kind: "interval-unavailable" };
        }
        priceIds.push(retained);
      }
      continue;
    }

    if (
      isBilledOnAnotherSubscription({
        catalog,
        entityId,
        snapshot,
        subscription,
      })
    ) {
      continue;
    }

    const entity = entityInCatalog(catalog, entityId);
    const seedPriceId = entity?.stripePriceId;

    if (!seedPriceId) {
      return { kind: "missing-price" };
    }

    const addedPriceId = priceIdForProductInInterval(
      snapshot,
      seedPriceId,
      targetInterval
    );
    if (!addedPriceId) {
      return { kind: "interval-unavailable" };
    }
    priceIds.push(addedPriceId);
  }

  // A trialing module the merchant left unselected is not a removal: the target is
  // declarative, so omitting it would delete the item and destroy the Trial Offer.
  const revoked = new Set(revokedTrialPriceIds ?? []);
  for (const trialPriceId of trialingPriceIdsOnSubscription(
    snapshot,
    subscription
  )) {
    if (revoked.has(trialPriceId)) {
      continue;
    }
    const retained = isCadenceChange
      ? priceIdForProductInInterval(snapshot, trialPriceId, targetInterval)
      : trialPriceId;
    if (!retained) {
      return { kind: "interval-unavailable" };
    }
    priceIds.push(retained);
  }

  return { kind: "ok", priceIds: unique(priceIds) };
};

const completionAnalytics = ({
  changeType,
  selection,
  snapshot,
}: {
  changeType: BillingCommandCompletionChangeType;
  selection: SelectionState;
  snapshot: BillingSnapshot;
}): BillingCommandAnalyticsEvent => {
  const quote = quoteSelection(snapshot, selection);

  return {
    type: "billing-plan-change-completed",
    billingCycle: cadenceName(selection.cadence),
    changeType,
    moduleIds: [...selection.moduleIds],
    planId: selection.planId ?? null,
    totalAmount:
      selection.cadence === "year" ? quote.intervalTotal : quote.monthlyTotal,
  };
};

const approvalStarted = ({
  changeType,
  selection,
}: {
  changeType: BillingCommandCompletionChangeType;
  selection: SelectionState;
}): BillingCommandAnalyticsEvent => ({
  type: "billing-shopify-approval-started",
  billingCycle: cadenceName(selection.cadence),
  changeType,
  moduleIds: [...selection.moduleIds],
  planId: selection.planId ?? null,
});

// Only commands that mint or replace a Shopify AppSubscription can round-trip
// through Shopify's approval UI. Lifecycle unwinds (cancel/pause/release)
// resolve internally on the backend, so arming the approval timer for them
// guarantees a spurious 60s "couldn't redirect you to Shopify" failure.
const MAY_AWAIT_SHOPIFY_APPROVAL_BY_OPERATION = {
  create: true,
  "add-module": true,
  "remove-module": true,
  "cancel-trial": true,
  "upgrade-unlimited": true,
  "upgrade-smart-pricing": true,
  cancel: false,
  pause: false,
  release: false,
  "apply-coupon": false,
  "cancel-pending-removal": false,
} as const satisfies Record<BillingCommandOperation, boolean>;

const providerPolicy = ({
  analytics,
  approval,
  operation,
  provider,
  snapshot,
}: {
  analytics: MutableAnalytics;
  // The selection the merchant would be approving, or null for an operation
  // that has no selection to describe because it can never redirect.
  approval: {
    changeType: BillingCommandCompletionChangeType;
    selection: SelectionState;
  } | null;
  operation: BillingCommandOperation;
  provider: "shopify" | "stripe" | null;
  snapshot: BillingSnapshot;
}): Pick<
  PlannedBillingCommand,
  | "analytics"
  | "approvalAnalytics"
  | "approvalTimeoutMs"
  | "mayAwaitShopifyApproval"
  | "completionAnalytics"
  | "provider"
  | "timeout"
> => {
  const isShopifyBilled =
    provider === "shopify" ||
    (provider === null && snapshot.subscriptionAccessData.org.shopId != null);
  const mayAwaitShopifyApproval =
    isShopifyBilled && MAY_AWAIT_SHOPIFY_APPROVAL_BY_OPERATION[operation];

  return {
    provider,
    mayAwaitShopifyApproval,
    // Accepting the mutation only queues a Shopify change; it becomes real
    // either when the merchant approves it and the plan intent applies, or
    // when the backend applies it under the live cap. Its completion events
    // therefore leave `onSuccess` and wait for whichever activation happens.
    analytics: mayAwaitShopifyApproval
      ? { ...analytics, onSuccess: [] }
      : analytics,
    completionAnalytics: mayAwaitShopifyApproval ? analytics.onSuccess : [],
    approvalAnalytics:
      mayAwaitShopifyApproval && approval ? [approvalStarted(approval)] : [],
    approvalTimeoutMs: mayAwaitShopifyApproval ? 60_000 : null,
    timeout: mayAwaitShopifyApproval
      ? {
          analytics: [
            checkoutError({
              errorType: "shopify_timeout",
              errorMessage: "Shopify approval URL not received within timeout",
              step: "payment",
            }),
          ],
          feedback: errorFeedback(
            "We couldn't redirect you to Shopify for approval. Please try again."
          ),
        }
      : null,
  };
};

const changeTypeForOperation = (operation: ProductSetOperation) =>
  operation === "add-module"
    ? "add_module"
    : operation === "remove-module" || operation === "cancel-trial"
      ? "remove_module"
      : "upgrade_smart_pricing_tier";

/**
 * The advertised eligible path for a Smart Pricing tier swap, matched by the **product**
 * the swap moves to.
 *
 * Only the Smart Pricing lane needs a path: it is the one operation whose timing comes
 * from the advertisement rather than from a fixed rule (`plannedTimingFor`). So all the
 * path has to establish is that policy accepts this tier — which is a fact about the
 * product, not about one of its prices.
 *
 * Matching a price id would tie the answer to cadence. The backend only builds candidates
 * from prices whose `interval` equals the subscription's current one, so a monthly org is
 * advertised the monthly tier price; a submit that upgrades the tier *and* switches to
 * annual resolves an annual price that appears in no advertised target, and the merchant
 * was refused a change policy had already accepted.
 *
 * Whole-target equality would be wrong for a second reason: the backend builds its target
 * from the subscription's live prices while the planner builds its own from the Selection,
 * so the two legitimately differ. CONTEXT.md makes paths advisory ("a Command matching no
 * advertised path is not thereby invalid") — policy re-evaluates whatever target the
 * Command carries.
 */
const findSmartPricingTierPath = (
  snapshot: BillingSnapshot,
  addedPrice: LivePriceDto
): RawUpgradeDowngradePath | undefined => {
  const addedProductPriceIds = new Set(
    snapshot.livePrices
      .filter(({ product }) => product.id === addedPrice.product.id)
      .flatMap(({ id, priceIds }) => [id, ...priceIds])
  );

  return snapshot.subscriptionAccessData.upgradeDowngradePaths.eligible.find(
    (path) =>
      path.changeType === "upgrade_smart_pricing_tier" &&
      path.target.priceIds.some((priceId) => addedProductPriceIds.has(priceId))
  );
};

const pathTiming = (
  path: RawUpgradeDowngradePath | undefined
): "immediate" | "period_end" => path?.effectiveTimingOptions[0] ?? "immediate";

const plannedTimingFor = (
  operation: ProductSetOperation,
  path: RawUpgradeDowngradePath | undefined
): "immediate" | "period_end" => {
  switch (operation) {
    case "upgrade-smart-pricing":
      return pathTiming(path);
    // A self-serve module removal never prorates or credits: the merchant
    // keeps access and we keep payment through the current billing period.
    case "remove-module":
      return "period_end";
    case "add-module":
      return "immediate";
    // A cascade-granted trial was never invoiced, so there's no period to keep access through.
    case "cancel-trial":
      return "immediate";
  }
};

const planCreate = ({
  nowSec,
  selection,
  snapshot,
  stagedCouponId,
}: {
  nowSec: number;
  selection: SelectionState;
  snapshot: BillingSnapshot;
  stagedCouponId: string | null;
}): BillingCommandPlan => {
  const selectedEntityIds = selectionEntityIds(selection);
  if (selectedEntityIds.length === 0) {
    return NO_CHANGES;
  }

  const catalog = buildBillingCatalog(snapshot, selection.cadence);
  const resolved = selectedEntityIds.map((entityId) => {
    const entity = entityInCatalog(catalog, entityId);
    const livePrice = entity
      ? livePriceForEntity(snapshot, entity, selection.cadence)
      : undefined;

    return { entityId, livePrice };
  });
  if (resolved.some(({ livePrice }) => livePrice == null)) {
    return operationBlocked({
      reason: "missing_price_id",
      message: "Failed to create your subscription. Please try again.",
      analytics: [
        checkoutError({
          errorType: "missing_price_ids",
          errorMessage: "No price IDs resolved for selected plan/modules",
          step: "plan_selection",
        }),
      ],
    });
  }

  const priceIds = resolved.map(({ livePrice }) => livePrice!.id);
  const products = resolved.map(({ entityId, livePrice }) =>
    optimisticProduct(livePrice!, entityId)
  );
  const analytics = emptyAnalytics();
  analytics.onSuccess = [
    completionAnalytics({ changeType: "new", selection, snapshot }),
  ];
  analytics.onError = [
    checkoutError({
      errorType: "mutation_error",
      errorMessage: "Create subscription command failed",
      step: "plan_selection",
    }),
  ];

  return {
    kind: "planned",
    operation: "create",
    request: {
      action: "create",
      requestedPlanIntentId: snapshot.subscriptionAccessData.planIntent?.id,
      requestedProductSetTarget: {
        version: PRODUCT_VERSION_2026,
        priceIds,
        ...(stagedCouponId ? { manualCouponIds: [stagedCouponId] } : {}),
      },
      requestedCadence: cadenceName(selection.cadence),
    },
    optimisticEvent: {
      type: "create-subscription",
      cadence: selection.cadence,
      createdAt: nowSec,
      addedEntityIds: selectedEntityIds,
      addedEntitlementKeySets: selectedEntityIds.map((entityId) =>
        entityKeys(catalog, entityId)
      ),
      removedEntityIds: [],
      removedEntitlementKeySets: [],
      targetEntitlementProfileKeys: selectedEntityIds,
      targetPriceIds: priceIds,
      products,
    },
    invalidateAfterMs: 10_000,
    ...providerPolicy({
      analytics,
      approval: { changeType: "new", selection },
      operation: "create",
      provider: null,
      snapshot,
    }),
    feedback: {
      success: PLAN_SAVED,
      error: errorFeedback(
        "Failed to create your subscription. Please try again."
      ),
    },
  };
};

const missingLinkForSelection = (): BlockedBillingCommand =>
  guard({
    reason: "missing_subscription_link",
    errorMessage: "Subscription link not found for billing change",
    step: "plan_selection",
  });

const planUnlimitedUpgrade = ({
  catalog,
  link,
  selection,
  snapshot,
  stagedCouponId,
  subscription,
}: {
  catalog: BillingCatalog;
  link: NonNullable<ReturnType<typeof subscriptionLink>>;
  selection: SelectionState;
  snapshot: BillingSnapshot;
  stagedCouponId: string | null;
  subscription: Subscription;
}): BillingCommandPlan => {
  const path =
    snapshot.subscriptionAccessData.upgradeDowngradePaths.eligible.find(
      ({ changeType }) => changeType === "upgrade_to_blue"
    );
  if (!path) {
    return operationBlocked({
      reason: "missing_upgrade_path",
      message: "Upgrade path is unavailable. Please refresh and try again.",
      analytics: [
        checkoutError({
          errorType: "missing_upgrade_path",
          errorMessage: "Upgrade to unlimited path not available",
          step: "plan_selection",
        }),
      ],
    });
  }

  // The price comes from the catalog's own Unlimited plan at the Selection's cadence — the
  // same row the dialog quotes — never from `path.target.priceIds`. The backend builds a
  // path only from prices whose interval matches the subscription's current one, so a
  // monthly org is advertised the monthly price; deriving the target from it billed monthly
  // against an annual quote. `formatPlans` already resolves this correctly (it pivots the
  // advertised price to the requested cadence through `matchLivePriceByIds`), so there is
  // nothing left here to re-derive.
  //
  // No fallback to the advertised price: if the plan has no row at this cadence the dialog
  // could not have offered it, and billing the other cadence is the defect, not a recovery.
  const unlimitedPlan = entityInCatalog(catalog, UNLIMITED_ID);
  const unlimitedLivePrice = unlimitedPlan
    ? livePriceForEntity(snapshot, unlimitedPlan, selection.cadence)
    : undefined;
  if (!unlimitedLivePrice) {
    return operationBlocked({
      reason: "missing_price_id",
      message: "Failed to upgrade to Unlimited. Please try again.",
      analytics: [
        checkoutError({
          errorType: "missing_price_id",
          errorMessage: `No ${cadenceName(selection.cadence)} price found for Unlimited`,
          step: "plan_selection",
        }),
      ],
    });
  }
  const unlimitedPriceIds = [unlimitedLivePrice.id];

  // Unlimited replaces every module item with the one plan price, so without this it
  // would also delete the riders — cancelling a paid add-on as a side effect of an
  // upgrade. The plan price is resolved at the Selection's cadence, so an upgrade that
  // also switches cadence has to move the riders with it.
  const priceIds = withRiderPriceIdsInCadence({
    cadence: selection.cadence,
    isCadenceChange: isCadenceChangeFor(subscription, selection.cadence),
    selectedPriceIds: unlimitedPriceIds,
    snapshot,
    subscription,
  });
  if (!priceIds) {
    return unconvertibleIntervalBlock({
      step: "plan_selection",
      subject: "rider",
    });
  }

  const analytics = emptyAnalytics();
  analytics.onSuccess = [
    completionAnalytics({ changeType: "upgrade", selection, snapshot }),
  ];
  analytics.onError = [
    checkoutError({
      errorType: "mutation_error",
      errorMessage: "Upgrade to unlimited command failed",
      step: "plan_selection",
    }),
  ];
  // The one row resolved above, so the optimistic product cannot name a different price or
  // product than the target bills. Searching the feed a second time for anything in the
  // target is what let a rider win the match — the feed arrives sorted cheapest-first, so a
  // cheap add-on row came before Unlimited's, and the overlay claimed that add-on's Stripe
  // product and name as the merchant's new plan.
  const unlimitedProducts = [
    optimisticProduct(unlimitedLivePrice, UNLIMITED_ID),
  ];

  return {
    kind: "planned",
    operation: "upgrade-unlimited",
    request: {
      action: "upgrade",
      requestedPlanIntentId: snapshot.subscriptionAccessData.planIntent?.id,
      stripeSubscriptionLinkId: link.id,
      billingProvider: link.billingProvider,
      changeType: "upgrade_to_blue",
      requestedEffectiveTiming: pathTiming(path),
      requestedProductSetTarget: {
        version: PRODUCT_VERSION_2026,
        priceIds,
        ...(stagedCouponId ? { addCouponId: stagedCouponId } : {}),
      },
    },
    optimisticEvent: {
      type: "change-product-set",
      operation: "upgrade-unlimited",
      stripeSubscriptionId: subscription.stripeSubscriptionId,
      addedEntityIds: [UNLIMITED_ID],
      addedEntitlementKeySets: [entityKeys(catalog, UNLIMITED_ID)],
      removedEntityIds: [],
      removedEntitlementKeySets: [],
      targetEntitlementProfileKeys: targetEntitlements({
        addedEntityIds: [UNLIMITED_ID],
        removedEntityIds: [],
        catalog,
        snapshot,
      }),
      targetPriceIds: priceIds,
      products: unlimitedProducts,
    },
    invalidateAfterMs: 0,
    ...providerPolicy({
      analytics,
      approval: { changeType: "upgrade", selection },
      operation: "upgrade-unlimited",
      provider: link.billingProvider,
      snapshot,
    }),
    feedback: {
      success: PLAN_SAVED,
      error: errorFeedback("Failed to upgrade to Unlimited. Please try again."),
    },
  };
};

/**
 * Refuses a change against a subscription this catalog cannot fully account for.
 *
 * The merchant's Selection is only a faithful picture of their plan when every price
 * they are billed on resolves to something the dialog showed them. When one does not,
 * both possible submits are wrong — keeping it bills a module the picker never offered,
 * dropping it deletes a module nobody asked to remove — so stop and report instead.
 * The partial twin of `isLivePricesCatalogUnusable`, which already hard-errors the
 * whole screen when *no* module price resolves.
 */
const unaccountedPricesBlock = ({
  catalog,
  snapshot,
  subscription,
}: {
  catalog: BillingCatalog;
  snapshot: BillingSnapshot;
  subscription: Subscription;
}): BlockedBillingCommand | null => {
  const unaccounted = unaccountedSubscriptionPriceIds(
    snapshot,
    catalog,
    subscription
  );
  if (unaccounted.length === 0) {
    return null;
  }

  return operationBlocked({
    reason: "unaccounted_subscription_prices",
    message:
      "We can't read your current plan, so we haven't changed it. Please contact support and we'll sort it out.",
    analytics: [
      checkoutError({
        errorType: "unaccounted_subscription_prices",
        errorMessage: `Subscription prices absent from the module catalog: ${unaccounted.join(", ")}`,
        step: "confirmation",
      }),
    ],
  });
};

const planProductSetChange = ({
  addedEntityIds,
  directRemove,
  link,
  operation,
  path,
  removedEntityIds,
  selection,
  snapshot,
  stagedCouponId = null,
  subscription,
  targetPriceIds,
}: {
  addedEntityIds: readonly string[];
  directRemove: boolean;
  link: NonNullable<ReturnType<typeof subscriptionLink>>;
  operation: ProductSetOperation;
  path?: RawUpgradeDowngradePath;
  removedEntityIds: readonly string[];
  selection: SelectionState;
  snapshot: BillingSnapshot;
  stagedCouponId?: string | null;
  subscription: Subscription;
  targetPriceIds: readonly string[];
}): PlannedBillingCommand => {
  const catalog = buildBillingCatalog(snapshot, selection.cadence);
  // Always the Selection-derived target, including for a Smart Pricing tier swap. The
  // matched eligible path contributes timing only: the backend advertises its target as
  // `unique([...activePriceIds, added])`, i.e. subscription-derived, so substituting it
  // here would reintroduce exactly the divergence the Selection-derived target closes.
  // Paths are advisory anyway — policy re-evaluates whatever target the Command carries.
  const finalPriceIds = [...targetPriceIds];
  // Selecting a module that is still trialing IS the confirmation. The trialing
  // price is pinned into every target regardless, so only this marker tells the
  // backend the merchant chose to keep it.
  const trialingOnSubscription = new Set(
    trialingPriceIdsOnSubscription(snapshot, subscription)
  );
  const confirmedTrialPriceIds =
    operation === "cancel-trial"
      ? []
      : unique(
          selection.moduleIds.flatMap((entityId) =>
            entityPriceIdsOnSubscription({
              catalog,
              entityId,
              snapshot,
              subscription,
            }).filter((priceId) => trialingOnSubscription.has(priceId))
          )
        );
  // Removing a module whose trial was confirmed does not change the product set:
  // the module keeps running free until its trial end, and this marker asks the
  // backend to re-park the cancellation that the confirmation released, so it is
  // removed at that boundary instead of converting to paid. Gated on the
  // deselection itself (removedEntityIds), not on `operation`: the net-direction
  // tie-break (adds vs. removes) can label a mixed add+remove submission
  // "add-module" even though it also drops a trialing module, and that drop must
  // still be parked.
  const unconfirmedTrialPriceIds =
    operation === "cancel-trial"
      ? []
      : unique(
          removedEntityIds
            .filter(
              (entityId) =>
                catalog.modules.find(({ id }) => id === entityId)
                  ?.trialUnconfirmable === true
            )
            .flatMap((entityId) =>
              entityPriceIdsOnSubscription({
                catalog,
                entityId,
                snapshot,
                subscription,
              }).filter((priceId) => trialingOnSubscription.has(priceId))
            )
        );
  // Cancelling a trial is the mirror image: the trialing price leaves the target,
  // and only this marker tells the backend the drop was commanded rather than
  // accidental, so the Trial Offer may be torn down and its parked cancellation
  // released.
  const revokedTrialPriceIds =
    operation === "cancel-trial"
      ? unique(
          removedEntityIds.flatMap((entityId) =>
            entityPriceIdsOnSubscription({
              catalog,
              entityId,
              snapshot,
              subscription,
            }).filter((priceId) => trialingOnSubscription.has(priceId))
          )
        )
      : [];
  const changeType = changeTypeForOperation(operation);
  const action =
    operation === "remove-module" || operation === "cancel-trial"
      ? "downgrade"
      : "upgrade";
  const cadenceChanged =
    getCurrentSelection(snapshot).cadence !== selection.cadence;
  const optimisticProducts = selectedProducts({
    catalog,
    entityIds:
      cadenceChanged && operation !== "upgrade-smart-pricing"
        ? selection.moduleIds
        : addedEntityIds,
    snapshot,
  });
  const analytics = emptyAnalytics();
  const completionChangeType: BillingCommandCompletionChangeType =
    operation === "add-module"
      ? "add_module"
      : operation === "remove-module"
        ? "remove_module"
        : "upgrade";
  let errorMessage: string;

  if (directRemove) {
    const entity = entityInCatalog(catalog, removedEntityIds[0]);
    // The module set as the merchant sees it, so the count these events report matches
    // the rows on the card and the `started` half of the same funnel.
    const remainingModuleIds = presentedActiveModules(catalog)
      .filter(({ id }) => !removedEntityIds.includes(id))
      .map(({ id }) => id);
    analytics.onStart = [
      {
        type: "billing-plan-downgrade-started",
        activeModuleIds: remainingModuleIds,
        currentPlanId:
          catalog.currentPlan?.id === UNLIMITED_ID ? UNLIMITED_ID : null,
      },
    ];
    analytics.onSuccess = [
      {
        type: "billing-module-removed",
        moduleName: entity?.name ?? removedEntityIds[0],
        remainingModuleIds,
      },
    ];
    errorMessage =
      operation === "cancel-trial"
        ? `Failed to end the ${entity?.name ?? removedEntityIds[0]} trial`
        : `Failed to remove ${entity?.name ?? removedEntityIds[0]}`;
  } else {
    analytics.onSuccess = [
      completionAnalytics({
        changeType: completionChangeType,
        selection,
        snapshot,
      }),
    ];
    if (operation === "add-module") {
      errorMessage = "Failed to add the module. Please try again.";
      analytics.onError = [
        checkoutError({
          errorType: "mutation_error",
          errorMessage: "Add module command failed",
          step: "module_selection",
        }),
      ];
    } else if (operation === "remove-module") {
      errorMessage = "Failed to remove the module. Please try again.";
      analytics.onError = [
        checkoutError({
          errorType: "mutation_error",
          errorMessage: "Remove module command failed",
          step: "module_selection",
        }),
      ];
    } else {
      errorMessage = "Failed to update Smart Pricing. Please try again.";
      analytics.onError = [
        checkoutError({
          errorType: "mutation_error",
          errorMessage: "Upgrade Smart Pricing tier command failed",
          step: "module_selection",
        }),
      ];
    }
  }

  return {
    kind: "planned",
    operation,
    request: {
      action,
      requestedPlanIntentId: snapshot.subscriptionAccessData.planIntent?.id,
      stripeSubscriptionLinkId: link.id,
      billingProvider: link.billingProvider,
      changeType,
      requestedEffectiveTiming: plannedTimingFor(operation, path),
      requestedProductSetTarget: {
        version: PRODUCT_VERSION_2026,
        priceIds: finalPriceIds,
        ...(stagedCouponId ? { addCouponId: stagedCouponId } : {}),
      },
      ...(confirmedTrialPriceIds.length > 0
        ? {
            moduleTrialConfirm: confirmedTrialPriceIds.map(
              (confirmedPriceId) => ({ confirmedPriceId })
            ),
          }
        : {}),
      ...(revokedTrialPriceIds.length > 0
        ? {
            moduleTrialRevoke: revokedTrialPriceIds.map((revokedPriceId) => ({
              revokedPriceId,
            })),
          }
        : {}),
      ...(unconfirmedTrialPriceIds.length > 0
        ? {
            moduleTrialUnconfirm: unconfirmedTrialPriceIds.map(
              (unconfirmedPriceId) => ({ unconfirmedPriceId })
            ),
          }
        : {}),
    },
    optimisticEvent: {
      type: "change-product-set",
      operation,
      stripeSubscriptionId: subscription.stripeSubscriptionId,
      addedEntityIds: [...addedEntityIds],
      addedEntitlementKeySets: addedEntityIds.map((entityId) =>
        entityKeys(catalog, entityId)
      ),
      removedEntityIds: [...removedEntityIds],
      removedEntitlementKeySets: removedEntityIds.map((entityId) =>
        entityKeys(catalog, entityId)
      ),
      targetEntitlementProfileKeys: targetEntitlements({
        addedEntityIds,
        removedEntityIds,
        catalog,
        snapshot,
      }),
      targetPriceIds: finalPriceIds,
      products: optimisticProducts,
    },
    invalidateAfterMs: operation === "upgrade-smart-pricing" ? 0 : 10_000,
    ...providerPolicy({
      analytics,
      approval: { changeType: completionChangeType, selection },
      operation,
      provider: link.billingProvider,
      snapshot,
    }),
    feedback: {
      success:
        operation === "remove-module" || operation === "cancel-trial"
          ? PLAN_SAVED_QUIET
          : PLAN_SAVED,
      error: errorFeedback(errorMessage),
    },
  };
};

const planSelectionChange = ({
  selection,
  snapshot,
  stagedCouponId,
}: {
  selection: SelectionState;
  snapshot: BillingSnapshot;
  stagedCouponId: string | null;
}): BillingCommandPlan => {
  const subscription = currentLiveSubscription(snapshot);
  if (!subscription) {
    return planCreate({ nowSec: 0, selection, snapshot, stagedCouponId });
  }

  if (selection.reactivatingEntityId) {
    return planRelease({
      entityId: selection.reactivatingEntityId,
      selection,
      snapshot,
    });
  }

  const currentSelection = getCommittedSelection(snapshot);
  const isMonthlyToAnnual =
    currentSelection.cadence === "month" && selection.cadence === "year";
  const catalog = buildBillingCatalog(snapshot, selection.cadence);
  if (isSelectionNoOp(selection, currentSelection) && !isMonthlyToAnnual) {
    return NO_CHANGES;
  }

  if (
    selection.planId === UNLIMITED_ID &&
    currentSelection.planId !== UNLIMITED_ID
  ) {
    const link = subscriptionLink(snapshot, subscription);
    if (!link) {
      return missingLinkForSelection();
    }

    // The Unlimited target is the plan price alone, so it deletes every other item on
    // the subscription — including one this catalog cannot account for. Same silent
    // deletion the guard exists to prevent, so this lane needs it too.
    const unaccounted = unaccountedPricesBlock({
      snapshot,
      catalog,
      subscription,
    });
    if (unaccounted) {
      return unaccounted;
    }

    return planUnlimitedUpgrade({
      catalog,
      link,
      selection,
      snapshot,
      stagedCouponId,
      subscription,
    });
  }

  const currentIds = new Set(currentSelection.moduleIds);
  const selectedIds = new Set(selection.moduleIds);
  const addedEntityIds = selection.moduleIds.filter(
    (id) => !currentIds.has(id)
  );
  const removedEntityIds = currentSelection.moduleIds.filter(
    (id) => !selectedIds.has(id)
  );

  if (selection.planId !== currentSelection.planId) {
    return guard({
      reason: "invalid_selection_change",
      errorMessage: "Requested plan change does not match an eligible path",
      step: "plan_selection",
    });
  }

  const commandSubscription = removedEntityIds[0]
    ? subscriptionForEntity({
        catalog,
        entityId: removedEntityIds[0],
        snapshot,
      })
    : subscription;
  if (!commandSubscription) {
    return operationBlocked({
      reason: "missing_product",
      message: "Unable to find this module. Please refresh and try again.",
      analytics: [
        checkoutError({
          errorType: "missing_module_product",
          errorMessage: "Module product not found for remove",
          step: "module_selection",
        }),
      ],
    });
  }
  const link = subscriptionLink(snapshot, commandSubscription);
  if (!link) {
    return missingLinkForSelection();
  }

  // Scoped to the subscription this Command actually edits — a removal can retarget it
  // at a different one. Guarding the whole account instead would refuse a change because
  // of a price on a subscription the Command never touches.
  const unaccounted = unaccountedPricesBlock({
    snapshot,
    catalog,
    subscription: commandSubscription,
  });
  if (unaccounted) {
    return unaccounted;
  }

  for (const entityId of removedEntityIds) {
    const entityPricesToRemove = entityPriceIdsOnSubscription({
      catalog,
      entityId,
      snapshot,
      subscription: commandSubscription,
    });
    if (entityPricesToRemove.length === 0) {
      const entitySubscription = subscriptionForEntity({
        catalog,
        entityId,
        snapshot,
      });
      // One dialog submit is one command, so removals that span subscriptions
      // cannot be honored at all — say that instead of blaming missing data.
      const billedElsewhere =
        entitySubscription !== undefined &&
        entitySubscription.stripeSubscriptionId !==
          commandSubscription.stripeSubscriptionId;

      return operationBlocked({
        reason: "missing_product",
        message: billedElsewhere
          ? "These modules are billed on separate subscriptions. Please remove them one at a time."
          : "Unable to find this module. Please refresh and try again.",
        analytics: [
          checkoutError({
            errorType: billedElsewhere
              ? "cross_subscription_remove"
              : "missing_module_product",
            errorMessage: billedElsewhere
              ? "Removed modules span multiple subscriptions"
              : "Module product not found for remove",
            step: "module_selection",
          }),
        ],
      });
    }
  }

  const isSmartPricingReplacement =
    addedEntityIds.length === 1 &&
    removedEntityIds.length === 1 &&
    SMART_PRICING_VARIANTS.has(addedEntityIds[0]) &&
    SMART_PRICING_VARIANTS.has(removedEntityIds[0]);
  const isCadenceChange = isCadenceChangeFor(
    commandSubscription,
    selection.cadence
  );
  const selectionTarget = targetPriceIdsForSelection({
    catalog,
    isCadenceChange,
    selection,
    snapshot,
    subscription: commandSubscription,
  });
  if (selectionTarget.kind === "interval-unavailable") {
    return unconvertibleIntervalBlock({
      step: "module_selection",
      subject: "module",
    });
  }
  if (selectionTarget.kind === "missing-price") {
    return operationBlocked({
      reason: "missing_price_id",
      message:
        "Unable to find pricing for this module. Please refresh and try again.",
      analytics: [
        checkoutError({
          errorType: "missing_price_id",
          errorMessage: "No price ID found for selected module",
          step: "module_selection",
        }),
      ],
    });
  }
  const selectedPriceIds = selectionTarget.priceIds;

  // Emptiness is judged on the Selection alone, before riders. A merchant holding a
  // rider who deselects everything must still hit this, not submit a rider-only target
  // that deletes every module and leaves the rider behind.
  if (selectedPriceIds.length === 0) {
    return operationBlocked({
      reason: "empty_target",
      message: "Keep at least one module, or cancel your plan instead.",
      analytics: [
        checkoutError({
          errorType: "guard_failure",
          errorMessage: "No price IDs remain after removing module",
          step: "confirmation",
        }),
      ],
    });
  }

  const targetPriceIds = withRiderPriceIdsInCadence({
    cadence: selection.cadence,
    isCadenceChange,
    selectedPriceIds,
    snapshot,
    subscription: commandSubscription,
  });
  if (!targetPriceIds) {
    return unconvertibleIntervalBlock({
      step: "module_selection",
      subject: "rider",
    });
  }

  if (
    isMonthlyToAnnual &&
    addedEntityIds.length === 0 &&
    removedEntityIds.length === 0
  ) {
    return planProductSetChange({
      addedEntityIds,
      directRemove: false,
      link,
      operation: "add-module",
      removedEntityIds,
      selection,
      snapshot,
      stagedCouponId,
      subscription: commandSubscription,
      targetPriceIds,
    });
  }

  if (isSmartPricingReplacement) {
    const addedEntity = entityInCatalog(catalog, addedEntityIds[0]);
    const addedPrice = addedEntity
      ? livePriceForEntity(snapshot, addedEntity, selection.cadence)
      : undefined;
    const path = addedPrice
      ? findSmartPricingTierPath(snapshot, addedPrice)
      : undefined;
    if (!path) {
      return operationBlocked({
        reason: "missing_upgrade_path",
        message:
          "Unable to update Smart Pricing. Please refresh and try again.",
        analytics: [
          checkoutError({
            errorType: "missing_upgrade_path",
            errorMessage: "Smart Pricing upgrade path not available",
            step: "module_selection",
          }),
        ],
      });
    }

    return planProductSetChange({
      addedEntityIds,
      directRemove: false,
      link,
      operation: "upgrade-smart-pricing",
      path,
      removedEntityIds,
      selection,
      snapshot,
      stagedCouponId,
      subscription: commandSubscription,
      targetPriceIds,
    });
  }

  // The command acts on the requested Product-set target, not on the diff, so
  // any selection the dialog can express is one command. changeType only names
  // the net direction of the change.
  if (addedEntityIds.length > 0 || removedEntityIds.length > 0) {
    const operation: ProductSetOperation =
      addedEntityIds.length >= removedEntityIds.length
        ? "add-module"
        : "remove-module";

    // No path is looked up: an add is always immediate and a removal always lands at
    // period end (`plannedTimingFor`), so the advertisement has nothing to contribute.
    return planProductSetChange({
      addedEntityIds,
      directRemove: false,
      link,
      operation,
      removedEntityIds,
      selection,
      snapshot,
      stagedCouponId,
      subscription: commandSubscription,
      targetPriceIds,
    });
  }

  return guard({
    reason: "invalid_selection_change",
    errorMessage: "Requested selection does not match an eligible change",
    step: "module_selection",
  });
};

const entityActionTarget = ({
  entityId,
  errorMessage,
  snapshot,
  step,
}: {
  entityId: string;
  errorMessage: string;
  snapshot: BillingSnapshot;
  step: "confirmation" | "module_selection" | "plan_selection";
}):
  | {
      catalog: BillingCatalog;
      link: NonNullable<ReturnType<typeof subscriptionLink>>;
      subscription: Subscription;
    }
  | BlockedBillingCommand => {
  // Located with a catalog at the account's cadence, because locating is
  // cadence-independent: `subscriptionForEntity` reads only the entity's variant keys and
  // the current plan's id off it.
  const accountCadence = currentCadence(snapshot);
  const locatorCatalog = buildBillingCatalog(snapshot, accountCadence);
  const entitySubscription = subscriptionForEntity({
    catalog: locatorCatalog,
    entityId,
    snapshot,
  });
  if (!entitySubscription) {
    return guard({ reason: "missing_subscription", errorMessage, step });
  }
  const link = subscriptionLink(snapshot, entitySubscription);
  if (!link) {
    return guard({
      reason: "missing_subscription_link",
      errorMessage,
      step,
    });
  }

  const entityCadence = subscriptionCadence(entitySubscription);
  const catalog =
    entityCadence === accountCadence
      ? locatorCatalog
      : buildBillingCatalog(snapshot, entityCadence);

  return { catalog, link, subscription: entitySubscription };
};

const isBlockedTarget = (
  target: ReturnType<typeof entityActionTarget> | BlockedBillingCommand
): target is BlockedBillingCommand =>
  "kind" in target && target.kind === "blocked";

const planCancel = ({
  entityId,
  expectedCancellationAt,
  snapshot,
  timing,
}: {
  entityId: string;
  expectedCancellationAt: number;
  snapshot: BillingSnapshot;
  timing: "immediate" | "scheduled";
}): BillingCommandPlan => {
  const target = entityActionTarget({
    entityId,
    errorMessage: "Subscription link not found for cancel",
    snapshot,
    step: "confirmation",
  });
  if (isBlockedTarget(target)) return target;

  const { catalog, link, subscription } = target;
  const analytics = emptyAnalytics();
  analytics.onSuccess = [
    {
      type: "billing-plan-cancel-confirmed",
      // Same set the `billing_plan_cancel_started` half of this funnel reports, which
      // the card builds from the rows it rendered.
      activeModuleIds: presentedActiveModules(catalog).map(({ id }) => id),
      currentPlanId: entityId,
    },
  ];

  return {
    kind: "planned",
    operation: "cancel",
    request: {
      action: "cancel",
      stripeSubscriptionLinkId: link.id,
      billingProvider: link.billingProvider,
      reasons: [
        {
          value: "other",
          text: "Cancelled via billing settings",
          items: [],
        },
      ],
      ...(timing === "scheduled" ? { cancelAt: expectedCancellationAt } : {}),
    },
    optimisticEvent: {
      type: "cancel-subscription",
      entityId,
      stripeSubscriptionId: subscription.stripeSubscriptionId,
      cancelAt: expectedCancellationAt,
    },
    invalidateAfterMs: 10_000,
    ...providerPolicy({
      analytics,
      approval: null,
      operation: "cancel",
      provider: link.billingProvider,
      snapshot,
    }),
    feedback: {
      success: timing === "scheduled" ? PLAN_SAVED_QUIET : null,
      error: errorFeedback("Failed to cancel the plan"),
    },
  };
};

const planPause = ({
  entityId,
  expectedPauseAt,
  snapshot,
}: {
  entityId: string;
  expectedPauseAt: number | null;
  snapshot: BillingSnapshot;
}): BillingCommandPlan => {
  const scheduled = expectedPauseAt != null;
  const target = entityActionTarget({
    entityId,
    errorMessage: scheduled
      ? "Subscription link not found for scheduled pause"
      : "Subscription link not found for pause",
    snapshot,
    step: "confirmation",
  });
  if (isBlockedTarget(target)) return target;

  const { link, subscription } = target;

  return {
    kind: "planned",
    operation: "pause",
    request: {
      action: "pause",
      stripeSubscriptionLinkId: link.id,
      billingProvider: link.billingProvider,
      reasons: [
        {
          value: "other",
          text: scheduled
            ? "Scheduled pause via billing settings"
            : "Paused via billing settings",
          items: [],
        },
      ],
      ...(scheduled ? { pauseAt: expectedPauseAt } : {}),
    },
    optimisticEvent: {
      type: "pause-subscription",
      entityId,
      stripeSubscriptionId: subscription.stripeSubscriptionId,
      pauseAt: expectedPauseAt,
      status: scheduled ? null : "paused",
    },
    invalidateAfterMs: scheduled ? 10_000 : 0,
    ...providerPolicy({
      analytics: emptyAnalytics(),
      approval: null,
      operation: "pause",
      provider: link.billingProvider,
      snapshot,
    }),
    feedback: {
      success: successFeedback(
        scheduled ? "The pause has been scheduled" : "Your plan has been paused"
      ),
      error: errorFeedback(
        scheduled ? "Failed to schedule the pause" : "Failed to pause the plan"
      ),
    },
  };
};

const planRelease = ({
  entityId,
  selection,
  snapshot,
}: {
  entityId: string;
  selection?: SelectionState;
  snapshot: BillingSnapshot;
}): BillingCommandPlan => {
  const target = entityActionTarget({
    entityId,
    errorMessage: "Subscription not found for release",
    snapshot,
    step: "confirmation",
  });
  if (isBlockedTarget(target)) return target;

  const { link, subscription } = target;
  const current = getCurrentSelection(snapshot);
  const analyticsSelection: SelectionState = selection ?? {
    ...current,
    reactivatingEntityId: entityId,
  };
  const analytics = emptyAnalytics();
  analytics.onSuccess = [
    completionAnalytics({
      changeType: "release_scheduled_cancellation",
      selection: analyticsSelection,
      snapshot,
    }),
  ];
  analytics.onError = [
    checkoutError({
      errorType: "mutation_error",
      errorMessage: "Release scheduled cancellation command failed",
      step: "confirmation",
    }),
  ];

  return {
    kind: "planned",
    operation: "release",
    request: {
      action: "release",
      stripeSubscriptionLinkId: link.id,
      billingProvider: link.billingProvider,
    },
    optimisticEvent: {
      type: "release-subscription",
      entityId,
      stripeSubscriptionId: subscription.stripeSubscriptionId,
    },
    invalidateAfterMs: 10_000,
    ...providerPolicy({
      analytics,
      approval: null,
      operation: "release",
      provider: link.billingProvider,
      snapshot,
    }),
    feedback: {
      success: PLAN_SAVED,
      error: errorFeedback("Failed to reactivate the plan"),
    },
  };
};

const planCancelPendingRemoval = ({
  entityId,
  snapshot,
  sourceCommandId,
}: {
  entityId: string;
  snapshot: BillingSnapshot;
  sourceCommandId: string;
}): BillingCommandPlan => {
  const target = entityActionTarget({
    entityId,
    errorMessage: "Module or subscription not found for reactivate",
    snapshot,
    step: "confirmation",
  });
  if (isBlockedTarget(target)) return target;

  const { link } = target;
  const current = getCurrentSelection(snapshot);
  const analytics = emptyAnalytics();
  analytics.onSuccess = [
    completionAnalytics({
      changeType: "cancel_pending_module_removal",
      selection: { ...current, reactivatingEntityId: entityId },
      snapshot,
    }),
  ];
  analytics.onError = [
    checkoutError({
      errorType: "mutation_error",
      errorMessage: "Cancel pending module removal command failed",
      step: "confirmation",
    }),
  ];

  return {
    kind: "planned",
    operation: "cancel-pending-removal",
    request: {
      action: "cancel_pending_module_removal",
      sourceCommandId,
    },
    optimisticEvent: {
      type: "cancel-pending-module-removal",
      commandId: sourceCommandId,
      entityId,
    },
    invalidateAfterMs: 10_000,
    ...providerPolicy({
      analytics,
      approval: null,
      operation: "cancel-pending-removal",
      provider: link.billingProvider,
      snapshot,
    }),
    feedback: {
      success: PLAN_SAVED,
      error: errorFeedback("Failed to reactivate the module"),
    },
  };
};

const planApplyCoupon = ({
  couponId,
  couponLabel,
  percentOff,
  amountOff,
  entityId,
  snapshot,
}: {
  couponId: string;
  couponLabel: string;
  percentOff?: number;
  amountOff?: number;
  entityId: string;
  snapshot: BillingSnapshot;
}): BillingCommandPlan => {
  const target = entityActionTarget({
    entityId,
    errorMessage: "Subscription link not found for coupon apply",
    snapshot,
    step: "confirmation",
  });
  if (isBlockedTarget(target)) return target;

  const { link, subscription } = target;

  return {
    kind: "planned",
    operation: "apply-coupon",
    request: {
      action: "apply_coupon",
      stripeSubscriptionLinkId: link.id,
      billingProvider: link.billingProvider,
      requestedProductSetTarget: {
        version: PRODUCT_VERSION_2026,
        // Unchanged — the backend rejects a coupon apply whose target diverges from this.
        priceIds: [...subscription.priceIds],
        addCouponId: couponId,
      },
    },
    optimisticEvent: {
      type: "apply-coupon",
      stripeSubscriptionId: subscription.stripeSubscriptionId,
      couponId,
      percentOff,
      amountOff,
    },
    invalidateAfterMs: 10_000,
    ...providerPolicy({
      analytics: emptyAnalytics(),
      approval: null,
      operation: "apply-coupon",
      provider: link.billingProvider,
      snapshot,
    }),
    feedback: {
      success: successFeedback(`Coupon applied: ${couponLabel}`),
      error: errorFeedback("Failed to apply coupon. Please try again."),
    },
  };
};

const planDirectRemove = ({
  entityId,
  snapshot,
}: {
  entityId: string;
  snapshot: BillingSnapshot;
}): BillingCommandPlan => {
  const target = entityActionTarget({
    entityId,
    errorMessage: "Module or subscription not found for remove",
    snapshot,
    step: "confirmation",
  });
  if (isBlockedTarget(target)) return target;

  const { catalog, link, subscription } = target;
  const pricesToRemove = entityPriceIdsOnSubscription({
    catalog,
    entityId,
    snapshot,
    subscription,
  });
  if (pricesToRemove.length === 0) {
    return guard({
      reason: "missing_product",
      errorMessage: "Module or subscription not found for remove",
      step: "confirmation",
    });
  }

  const unaccounted = unaccountedPricesBlock({
    snapshot,
    catalog,
    subscription,
  });
  if (unaccounted) {
    return unaccounted;
  }

  // The row action has no dialog to read a Selection from, so it synthesizes the one
  // the merchant would have submitted: today's Selection minus this module. The target
  // then comes from that Selection like every other lane's, so a row removal and the
  // same removal made in the dialog produce the identical Command.
  const current = getCommittedSelection(snapshot);
  const selection: SelectionState = {
    ...current,
    moduleIds: current.moduleIds.filter((id) => id !== entityId),
    reactivatingEntityId: null,
  };

  const selectionTarget = targetPriceIdsForSelection({
    // Built at the synthesized Selection's cadence, which is the account's. The removal
    // itself never re-prices (`isCadenceChange: false` below), so this cadence only
    // reaches a module the Selection keeps that this subscription does not bill — which
    // an Unlimited org has, because owning the plan marks every module active.
    catalog: buildBillingCatalog(snapshot, selection.cadence),
    // The row action has no cadence affordance and `selection` is a copy of the current
    // one, so this can never be a re-price. Stated rather than derived, so a Command
    // against a sibling subscription on another interval cannot re-price it either.
    isCadenceChange: false,
    selection,
    snapshot,
    subscription,
  });

  if (selectionTarget.kind === "interval-unavailable") {
    return unconvertibleIntervalBlock({
      step: "confirmation",
      subject: "module",
    });
  }

  if (selectionTarget.kind === "missing-price") {
    return guard({
      reason: "missing_price_id",
      errorMessage: "No price ID found for a module kept after removal",
      step: "confirmation",
    });
  }
  const selectedPriceIds = selectionTarget.priceIds;
  if (selectedPriceIds.length === 0) {
    return guard({
      reason: "empty_target",
      errorMessage: "No price IDs remain after removing module",
      step: "confirmation",
    });
  }

  // The total `withRiderPriceIds`, not the cadence-aware one: the row action has no cadence
  // affordance, so nothing here re-prices a rider and there is no failure to handle.
  const targetPriceIds = withRiderPriceIds(
    selectedPriceIds,
    snapshot,
    subscription
  );

  return planProductSetChange({
    addedEntityIds: [],
    directRemove: true,
    link,
    operation: "remove-module",
    removedEntityIds: [entityId],
    selection,
    snapshot,
    subscription,
    targetPriceIds,
  });
};

// Mirrors planDirectRemove — only the requested timing differs (see plannedTimingFor's cancel-trial case).
const planCancelTrial = ({
  entityId,
  snapshot,
}: {
  entityId: string;
  snapshot: BillingSnapshot;
}): BillingCommandPlan => {
  const target = entityActionTarget({
    entityId,
    errorMessage: "Module or subscription not found for trial cancellation",
    snapshot,
    step: "confirmation",
  });
  if (isBlockedTarget(target)) return target;

  const { catalog, link, subscription } = target;
  const pricesToRemove = entityPriceIdsOnSubscription({
    catalog,
    entityId,
    snapshot,
    subscription,
  });
  if (pricesToRemove.length === 0) {
    return guard({
      reason: "missing_product",
      errorMessage: "Module or subscription not found for trial cancellation",
      step: "confirmation",
    });
  }

  const unaccounted = unaccountedPricesBlock({
    snapshot,
    catalog,
    subscription,
  });
  if (unaccounted) {
    return unaccounted;
  }

  const current = getCommittedSelection(snapshot);
  const selection: SelectionState = {
    ...current,
    moduleIds: current.moduleIds.filter((id) => id !== entityId),
    reactivatingEntityId: null,
  };

  const selectionTarget = targetPriceIdsForSelection({
    catalog: buildBillingCatalog(snapshot, selection.cadence),
    isCadenceChange: false,
    // The trial being cancelled is a deliberate revocation, so it alone is not pinned.
    revokedTrialPriceIds: pricesToRemove,
    selection,
    snapshot,
    subscription,
  });

  if (selectionTarget.kind === "interval-unavailable") {
    return unconvertibleIntervalBlock({
      step: "confirmation",
      subject: "module",
    });
  }

  if (selectionTarget.kind === "missing-price") {
    return guard({
      reason: "missing_price_id",
      errorMessage:
        "No price ID found for a module kept after trial cancellation",
      step: "confirmation",
    });
  }
  const selectedPriceIds = selectionTarget.priceIds;
  if (selectedPriceIds.length === 0) {
    return guard({
      reason: "empty_target",
      errorMessage: "No price IDs remain after cancelling trial module",
      step: "confirmation",
    });
  }

  const targetPriceIds = withRiderPriceIds(
    selectedPriceIds,
    snapshot,
    subscription
  );

  return planProductSetChange({
    addedEntityIds: [],
    directRemove: true,
    link,
    operation: "cancel-trial",
    removedEntityIds: [entityId],
    selection,
    snapshot,
    subscription,
    targetPriceIds,
  });
};

export const planBillingCommand = ({
  intent,
  nowSec,
  snapshot,
  stagedCouponId = null,
}: BillingCommandPlannerInput): BillingCommandPlan => {
  if (billingCommandInProgress(snapshot)) {
    return commandInProgressPlan();
  }

  if (intent.type === "submit-selection") {
    const subscription = currentLiveSubscription(snapshot);
    return subscription
      ? planSelectionChange({
          selection: intent.selection,
          snapshot,
          stagedCouponId,
        })
      : planCreate({
          nowSec,
          selection: intent.selection,
          snapshot,
          stagedCouponId,
        });
  }

  if (intent.type === "cancel") {
    return planCancel({
      entityId: intent.entityId,
      expectedCancellationAt: intent.expectedCancellationAt,
      snapshot,
      timing: intent.timing,
    });
  }

  if (intent.type === "pause") {
    return planPause({
      entityId: intent.entityId,
      expectedPauseAt: intent.expectedPauseAt,
      snapshot,
    });
  }

  if (intent.type === "remove-module") {
    return planDirectRemove({ entityId: intent.entityId, snapshot });
  }

  if (intent.type === "cancel-trial") {
    return planCancelTrial({ entityId: intent.entityId, snapshot });
  }

  if (intent.type === "cancel-pending-removal") {
    return planCancelPendingRemoval({
      entityId: intent.entityId,
      snapshot,
      sourceCommandId: intent.sourceCommandId,
    });
  }

  if (intent.type === "apply-coupon") {
    return planApplyCoupon({
      couponId: intent.couponId,
      couponLabel: intent.couponLabel,
      percentOff: intent.percentOff,
      amountOff: intent.amountOff,
      entityId: intent.entityId,
      snapshot,
    });
  }

  return planRelease({ entityId: intent.entityId, snapshot });
};
